import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import {
  update,
  exportContentTemplatesToCSV,
  setArchived,
  paginatePropertyContentTemplates,
} from '@go-client/lib/api/contentTemplate';
import {
  ALL_CONTENT_TEMPLATE_TYPES,
  BareContentTemplate,
  ContentTemplate,
  ContentTemplateType,
  InfoContentTemplate,
} from '@go-shared/types/contentTemplate';
import {
  BubbleAccordion,
  BubbleBadge,
  BubbleButton,
  BubbleCheckbox,
  BubbleDebounce,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleInput,
  BubbleOverlay,
  BubbleTable,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

import './ContentLibrary.scss';
import Libraries from './Libraries';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canManageLibraries, canModifyMultiPropertyItem } from '@go-shared/lib/permissions';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import { PropertyTag } from '@go-shared/types/property';
import { hashStringToColor } from '@go-shared/lib/utils';
import {
  getLocaleDateTimeStringUTC,
  _dangerouslyParseUnknownStringToDate,
} from '@go-shared/lib/time';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import { CopyToClipboardButton } from '@go-client/components/CopyToClipboard';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import ContentTemplateFormik, {
  ContentTemplateFormikMode,
} from '@go-client/components/Formik/ContentTemplateFormik';
import BubbleDrawerBasicContainer from '@go-client/components/BubbleUI/BubbleDrawerHeader';
import { LibrariesSubNavigation } from '@go-client/components/Navigations/LibrariesSubNavigation';
import AccessGlanceButton from '@go-client/components/AccessGlanceButton';
import PropertyAccessFormik from '@go-client/components/Formik/PropertyAccessFormik';
import { SortOrder } from '@go-shared/types/userActionRecord';
import { DEFAULT_PER_PAGE, PaginationMetadata } from '@go-shared/types/pagination';
import { BubbleTablePaginationFooter } from '@go-client/components/BubbleUI/BubbleTablePaginationFooter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSortingIcon } from '@go-shared/lib/paginations';
import { Tooltip } from '@spark-labs/avocado-ui';
import { McmEndpoints } from '@go-client/components/Mcm/McmEndpoints';
import { readQs } from '@go-shared/lib/querystring';
import { isNotBasicContentTemplate } from '@go-shared/lib/contentTemplate';
import { camelToSentenceCase, stripHTML } from '@go-shared/lib/string';
import {
  BrandContentAttribute,
  BrandContentLevel,
  BrandContentSegment,
} from '@go-shared/types/brand';

interface ContentLibraryDataBundle {
  tableItems: ContentTableItem[];
}

interface ContentTableItem {
  id: string;
  type: ContentTemplateType;
  name: string;
  value: string;
  updatedAt: string;
  tags: PropertyTag[];
  _source: ContentTemplate;
}

type ContentTemplateFilters = {
  tags: Array<BubbleTypeaheadValue<PropertyTag>>;
  types: Array<BubbleTypeaheadValue<ContentTemplateType>>;
  segments: Array<BubbleTypeaheadValue<BrandContentSegment>>;
  levels: Array<BubbleTypeaheadValue<BrandContentLevel>>;
  attributes: Array<BubbleTypeaheadValue<BrandContentAttribute>>;
};

type PaginationParams<T> = Partial<ContentTemplateFilters> & {
  page: number;
  perPage: number;
  sortBy: keyof T;
  sortDirection: SortOrder;
  archived: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
  propertyId?: string;
};

type VisibleFields = {
  [key: string]: boolean;
};

type ContentTableFields = BubbleTableFields<ContentTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

function convertToBare(item: ContentTemplate): BareContentTemplate {
  return {
    ...item,
    brand: item.brand._id,
    segment: item?.segment?._id,
    levels: item?.levels?.map(l => l._id) || [],
    attributes: item?.attributes?.map(a => a._id) || [],
    description:
      item.type === 'message' || item.type === 'info' || item.type === 'offer'
        ? (item as any).description
        : '',
    appearance:
      item.type === 'info' ? (item as unknown as InfoContentTemplate).appearance : 'default',
  } as unknown as BareContentTemplate;
}

export default function ContentLibrary(props: RouteComponentProps) {
  const { ...routeProps } = props;

  const { activeProperty: property } = useActiveProperty();
  const serverInfo = useServerInfo();

  const { user } = useUserInfo();

  const tagOpts = property.contentTags.map(t => ({
    key: t._id,
    item: t,
  }));
  const typeOpts = ALL_CONTENT_TEMPLATE_TYPES.map(t => ({ key: t, item: t }));
  const tagMap = mapBy(tagOpts, 'key');
  const segmentOpts = serverInfo.brand.contentSegments.map(s => ({ key: s._id, item: s }));
  const levelOpts = serverInfo.brand.contentLevels.map(l => ({ key: l._id, item: l }));
  const attributeOpts = serverInfo.brand.contentAttributes.map(a => ({ key: a._id, item: a }));

  const [loading, setLoading] = useState(false);
  const [dataBundle, setDataBundle] = useState<ContentLibraryDataBundle | undefined>(undefined);
  const [paginationMetadata, setPaginationMetadata] =
    useState<PaginationMetadata | undefined>(undefined);

  const [mode, setMode] = useState<ContentTemplateFormikMode | undefined>(undefined);
  const [showAccessForm, setShowAccessForm] = useState<BareContentTemplate | undefined>(undefined);
  const [filters, setFilters] = useState<ContentTemplateFilters>({
    tags: [],
    types: [],
    segments: [],
    levels: [],
    attributes: [],
  });
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});

  const [visibleFields, setVisibleFields] = useState<VisibleFields>({
    access: true,
    name: true,
    value: true,
    type: true,
    tags: true,
    endpoints: true,

    segment: true,
    levels: false,
    attributes: false,

    createdAt: true,
    actions: true,
  });

  const queries = readQs();

  const [paginationParams, setPaginationParams] = useState<PaginationParams<BareContentTemplate>>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    sortBy: 'createdAt',
    sortDirection: SortOrder.DESC,
    startDate: '',
    endDate: '',
    propertyId: property._id,
    archived: false,
    tags: [],
    types: [],
    segments: [],
    levels: [],
    attributes: [],
    search: typeof queries.search === 'string' ? queries.search : '',
  });

  const contentRef = useRef<HTMLDivElement | null>(null);
  // this will scroll the page to top when search is modified
  useEffect(() => {
    // We want to scroll to top only when we are in page 1 or paginationParams are reset
    if (paginationParams.page !== 1) {
      return;
    }

    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [paginationParams, contentRef]);

  useEffect(() => {
    if (property._id !== paginationParams.propertyId) {
      setPaginationParams({ ...paginationParams, page: 1, propertyId: property._id });
    }
  }, [property]);

  async function refresh() {
    setShowAccessForm(undefined);
    setLoading(true);
    await fetchDataBundle();
  }

  useEffect(() => {
    refresh();
  }, [paginationParams]);

  async function onSubmit() {
    setShowAccessForm(undefined);
    setMode(undefined);
    refresh();
  }

  async function fetchDataBundle() {
    try {
      const contentTemplates = await paginatePropertyContentTemplates({
        page: paginationParams.page,
        perPage: paginationParams.perPage,
        sortBy: paginationParams.sortBy,
        sortDirection: paginationParams.sortDirection,
        propertyId: property._id,
        ...(paginationParams.search && { search: paginationParams.search }),
        ...(paginationParams.archived && { archived: paginationParams.archived }),
        ...(paginationParams.tags && { tags: paginationParams.tags.map(t => t.item._id) }),
        ...(paginationParams?.types && {
          types: paginationParams.types.map(t => t.item),
        }),
        ...(paginationParams.segments && {
          segments: paginationParams.segments.map(s => s.item._id),
        }),
        ...(paginationParams.levels && { levels: paginationParams.levels.map(l => l.item._id) }),
        ...(paginationParams.attributes && {
          attributes: paginationParams.attributes.map(a => a.item._id),
        }),
        ...(paginationParams.startDate && { startDate: paginationParams.startDate }),
        ...(paginationParams.endDate && { endDate: paginationParams.endDate }),
      });

      setPaginationMetadata(contentTemplates.meta);

      const tableItems: ContentTableItem[] = contentTemplates.data.map(itm => {
        let updatedAt = '';

        if (itm.updatedAt) {
          const updatedAtDate = _dangerouslyParseUnknownStringToDate(itm.updatedAt);

          updatedAt = updatedAtDate ? getLocaleDateTimeStringUTC(updatedAtDate) : itm.updatedAt;
        }

        const tags = itm.tags.map(t => tagMap[t]?.item).filter(notUndefined);

        return {
          id: itm._id,
          type: itm.type || 'basic',
          name: itm.name,
          value: itm.content,
          updatedAt,
          tags,
          _source: itm,
        };
      });

      setDataBundle({
        tableItems:
          paginationParams.page > 1
            ? [...(dataBundle?.tableItems ?? []), ...tableItems]
            : tableItems,
      });
    } finally {
      setLoading(false);
    }
  }

  async function generateCSV() {
    const result = await exportContentTemplatesToCSV(property._id);
    if (result.location) {
      window.open(result.location, '_blank');
    }
  }

  async function handleFormArchive(item: ContentTemplate, state: boolean) {
    await setArchived({
      ...item,
      archived: state,
    });

    // no need to re-fetch, only remove the archived one from list
    setDataBundle({
      ...dataBundle,
      tableItems: (dataBundle?.tableItems || []).filter(ti => ti._source._id !== item._id),
    });
  }

  const tableFields: Array<ContentTableFields> = [
    {
      key: 'access',
      _toggleable: true,
    },
    {
      key: 'name',
      tdProps: { style: { minWidth: '200px' } },
      _toggleable: true,
    },
    {
      key: 'value',
      label: 'Description',
      tdProps: { style: { minWidth: '400px' } },
      _toggleable: true,
    },
    {
      key: 'type',
      _toggleable: true,
    },
    {
      key: 'tags',
      _toggleable: true,
    },
    {
      key: 'endpoints',
      _toggleable: true,
      thProps: { className: 'text-center' },
      tdProps: { className: 'text-center' },
    },
    {
      key: 'segment',
      _toggleable: true,
    },
    {
      key: 'levels',
      _toggleable: true,
    },
    {
      key: 'attributes',
      _toggleable: true,
    },
    {
      key: 'createdAt',
      behavior: BubbleTableFieldBehavior.NoWrap,
      thProps: {
        onClick: () => {
          const sortDirection =
            paginationParams.sortBy === 'createdAt'
              ? paginationParams.sortDirection * -1
              : SortOrder.DESC;
          setPaginationParams({ ...paginationParams, page: 1, sortBy: 'createdAt', sortDirection });
        },
      },
      render: () => (
        <span className="cursor-pointer">
          Created
          {paginationParams.sortBy === 'createdAt' && (
            <FontAwesomeIcon
              icon={getSortingIcon(paginationParams.sortDirection)}
              fixedWidth
              size={'sm'}
              className={'cursor-pointer'}
            />
          )}
        </span>
      ),
      _toggleable: true,
    },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
      _toggleable: true,
    },
  ].filter(notUndefined);

  const tableTemplates: Array<BubbleTableTemplate<ContentTableItem>> = [
    {
      key: 'access',
      render: item => (
        <Tooltip text="Click to manage the access">
          <AccessGlanceButton
            item={item._source}
            onClick={() => setShowAccessForm(convertToBare(item._source))}
          />
        </Tooltip>
      ),
    },
    {
      key: 'name',
      render: item => {
        return (
          <>
            <div>{item.name}</div>
            {item.id.length > 0 && <CopyToClipboardButton copyText={item.id} label="Copy ID" />}
          </>
        );
      },
    },
    {
      key: 'type',
      render: item => {
        return (
          <BubbleBadge className="mt-1 mr-1" color={hashStringToColor(item.type)}>
            {item.type}
          </BubbleBadge>
        );
      },
    },
    {
      key: 'segment',
      render: item => {
        if (item._source.segment) {
          return (
            <BubbleBadge
              key={item._source.segment._id}
              color={item._source.segment.color}
              className="mt-1 mr-1"
            >
              {item._source.segment.name}
            </BubbleBadge>
          );
        }

        return <></>;
      },
    },
    {
      key: 'levels',
      render: item => {
        let element = <></>;

        if (item._source.levels.length > 0) {
          element = (
            <>
              {item._source.levels.map(l => {
                return (
                  <BubbleBadge key={l._id} color={hashStringToColor(l.name)} className="mr-1">
                    {l.name}
                  </BubbleBadge>
                );
              })}
            </>
          );
        }

        return element;
      },
    },
    {
      key: 'attributes',
      render: item => {
        let element = <></>;

        if (item._source.attributes.length > 0) {
          element = (
            <>
              {item._source.attributes.map(a => {
                return (
                  <BubbleBadge key={a._id} color={hashStringToColor(a.name)} className="mr-1">
                    {a.name}
                  </BubbleBadge>
                );
              })}
            </>
          );
        }

        return element;
      },
    },
    {
      key: 'tags',
      render: item =>
        item.tags.length > 0 &&
        item.tags.map(t => (
          <BubbleBadge key={t._id} className="mt-1 mr-1" color={hashStringToColor(t.name)}>
            {t.name}
          </BubbleBadge>
        )),
    },
    {
      key: 'endpoints',
      render: item => <McmEndpoints template={item._source} />,
    },
    {
      key: 'createdAt',
      render: item => {
        let element = <></>;

        if (item._source.createdAt) {
          const createdAt = getLocaleDateTimeStringUTC(new Date(item._source.createdAt));
          const [date, time] = createdAt.split(', ');
          element = (
            <>
              <div className="block whitespace-nowrap">{date}</div>
              <div className="block whitespace-nowrap">{time}</div>
            </>
          );
        }

        return element;
      },
    },
    {
      key: 'actions',
      render: item => (
        <>
          <BubbleButton
            className="p-2-i"
            variant="navy"
            inverse
            icon="edit"
            onClick={() => setMode({ type: 'edit', edit: item._source })}
          />

          {canManageLibraries(user) && (
            <BubbleButton
              className="p-2-i"
              variant="slate"
              inverse
              icon="copy"
              onClick={() => setMode({ type: 'create', edit: item._source })}
            />
          )}
          {canModifyMultiPropertyItem(user, serverInfo, property._id, item._source) && (
            <>
              {!showDelete[item.id] && (
                <BubbleButton
                  className="p-2-i"
                  variant="dirt"
                  inverse
                  onClick={() => {
                    setShowDelete({ ...showDelete, [item.id]: true });
                  }}
                  icon="trash-alt"
                />
              )}
              {showDelete[item.id] && (
                <>
                  <BubbleButton
                    className="p-2-i"
                    variant="dirt"
                    inverse
                    onClick={() => handleFormArchive(item._source, !item._source.archived)}
                    icon="trash-check"
                  />
                  <BubbleButton
                    className="p-2-i"
                    variant="slate"
                    inverse
                    onClick={() => {
                      setShowDelete({ ...showDelete, [item.id]: false });
                    }}
                    icon="trash-xmark"
                  />
                </>
              )}
            </>
          )}
        </>
      ),
    },
    {
      key: 'value',
      render: itm => {
        if (isNotBasicContentTemplate(itm._source)) {
          return <div>{stripHTML(itm._source.description)}</div>;
        }

        return <div>{stripHTML(itm.value)}</div>;
      },
    },
  ];

  function headerFn() {
    return <LibrariesSubNavigation active="content" />;
  }

  const visibleTableFields = tableFields.filter(tf =>
    tf._visible !== undefined ? tf._visible : visibleFields[tf.key]
  );
  const toggleableTableFields = tableFields.filter(tf => tf._toggleable);

  function bodyFn() {
    return (
      <BubbleOverlay loading={loading}>
        <BubbleTable
          responsive
          striped
          headVariant="titanium"
          fields={visibleTableFields}
          items={dataBundle?.tableItems}
          stickyHeader={182}
          templates={tableTemplates}
          showPretitle
          pretitleClassName="p-0-i"
          showAllItems={true}
          onRowClick={itm => {
            setMode({ type: 'edit', edit: itm._source });
          }}
          pretitleRender={() => (
            <div className="px-4 pt-4 pb-1">
              <BubbleDebounce
                onTrigger={[
                  (search: string) => setPaginationParams({ ...paginationParams, page: 1, search }),
                  500,
                ]}
              >
                {({ debounce }) => (
                  <BubbleInput
                    type="text"
                    variant="titanium"
                    block
                    value={paginationParams.search}
                    inputClassName="outline-none-i"
                    onValue={debounce}
                    placeholder="Search..."
                  />
                )}
              </BubbleDebounce>
            </div>
          )}
        />

        {paginationMetadata && (
          <BubbleTablePaginationFooter
            paginationMetadata={paginationMetadata}
            perPage={paginationParams.perPage}
            onLoadMoreClick={() =>
              setPaginationParams({ ...paginationParams, page: paginationParams.page + 1 })
            }
            onPerPageChange={value =>
              setPaginationParams({ ...paginationParams, page: 1, perPage: value })
            }
          />
        )}
      </BubbleOverlay>
    );
  }

  function outerFn() {
    return (
      <>
        {mode && dataBundle && (
          <BubbleDrawer containerClassName="bg-eggshell" onClose={() => setMode(undefined)}>
            <BubbleDrawerBasicContainer
              title={
                mode.type === 'edit' ? `Edit Content - ${mode.edit?.name ?? ''}` : 'Create Content'
              }
              subtitle="Content Details"
            >
              <ContentTemplateFormik
                mode={mode}
                onSubmit={onSubmit}
                actionsAppearance="drawer-float"
                subnavClassName="-mx-8"
              />
            </BubbleDrawerBasicContainer>
          </BubbleDrawer>
        )}
        {showAccessForm && (
          <BubbleDrawer
            onClose={() => setShowAccessForm(undefined)}
            containerClassName="bg-eggshell"
          >
            <BubbleDrawerBasicContainer
              title={`Edit Content - ${showAccessForm?.name ?? ''}`}
              subtitle="Manage Access"
              onClose={() => setShowAccessForm(undefined)}
            >
              <PropertyAccessFormik
                edit={showAccessForm}
                makePropertyAccessItem={itm => ({ property: itm })}
                updateFn={update}
                actionsAppearance="drawer-float"
                subnavClassName="-mx-8"
                onSubmit={onSubmit}
              />
            </BubbleDrawerBasicContainer>
          </BubbleDrawer>
        )}
      </>
    );
  }

  function sideFn() {
    return (
      <>
        <div className="flex flex-col relative items-stretch p-4 bg-titanium-darker">
          <div className="grid gap-2">
            {canManageLibraries(user) && (
              <BubbleButton
                variant="lime"
                onClick={() =>
                  setMode({
                    type: 'create',
                  })
                }
                className="text-left"
                icon="sparkles"
              >
                Create Content
              </BubbleButton>
            )}
            <BubbleButton
              variant="slate"
              onClick={generateCSV}
              className="text-left"
              icon="file-export"
            >
              Export to CSV
            </BubbleButton>
          </div>
        </div>
        <BubbleAccordion
          variant="titanium"
          className="flex-grow flex flex-col border-t border-forest-darkest"
          labels={['Filters']}
          open={[0]}
          locked={[0]}
        >
          <div className="grid gap-4 p-4">
            <BubbleFormGroup label="Dates" labelClassName="text-white-i">
              <div className="grid grid-cols-1 gap-1">
                <BubbleDebounce
                  onTrigger={[
                    (date: string) => {
                      setPaginationParams({
                        ...paginationParams,
                        page: 1,
                        startDate: date,
                      });
                    },
                    500,
                  ]}
                >
                  {({ debounce }) => (
                    <BubbleInput
                      variant="titanium"
                      type="date"
                      value={paginationParams.startDate}
                      onValue={debounce}
                    />
                  )}
                </BubbleDebounce>
                <BubbleDebounce
                  onTrigger={[
                    (date: string) =>
                      setPaginationParams({ ...paginationParams, page: 1, endDate: date }),
                    500,
                  ]}
                >
                  {({ debounce }) => (
                    <BubbleInput
                      variant="titanium"
                      type="date"
                      value={paginationParams.endDate}
                      onValue={debounce}
                    />
                  )}
                </BubbleDebounce>
              </div>
            </BubbleFormGroup>
            <BubbleFormGroup label="Type" labelClassName="text-white-i">
              <BubbleTypeahead
                block
                variant="titanium"
                placeholder="Filter types shown..."
                menuClassName="max-h-80 overflow-y-auto"
                value={filters.types}
                options={typeOpts}
                onAdd={t => {
                  const types = [...filters.types, t];

                  setFilters({ ...filters, types });
                  setPaginationParams({ ...paginationParams, page: 1, types });
                }}
                onRemove={t => {
                  const types = filters.types.filter(t2 => t.key !== t2.key);

                  setFilters({ ...filters, types });
                  setPaginationParams({ ...paginationParams, page: 1, types });
                }}
                onClearAll={() => {
                  setFilters({ ...filters, types: [] });
                  setPaginationParams({ ...paginationParams, page: 1, types: [] });
                }}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Tags" labelClassName="text-white-i">
              <BubbleTypeahead
                block
                variant="titanium"
                placeholder="Filter tags shown..."
                menuClassName="max-h-80 overflow-y-auto"
                labelExtractor={opt => opt.name}
                value={filters.tags}
                options={tagOpts}
                onAdd={t => {
                  const tags = [...filters.tags, t];

                  setFilters({ ...filters, tags });
                  setPaginationParams({ ...paginationParams, page: 1, tags });
                }}
                onRemove={t => {
                  const tags = filters.tags.filter(t2 => t.key !== t2.key);

                  setFilters({ ...filters, tags });
                  setPaginationParams({ ...paginationParams, page: 1, tags });
                }}
                onClearAll={() => {
                  setFilters({ ...filters, tags: [] });
                  setPaginationParams({ ...paginationParams, page: 1, tags: [] });
                }}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Segment" labelClassName="text-white-i">
              <BubbleTypeahead
                block
                variant="titanium"
                placeholder="Filter segments shown..."
                menuClassName="max-h-80 overflow-y-auto"
                labelExtractor={opt => opt.name}
                value={filters.segments}
                options={segmentOpts}
                onAdd={s => {
                  const segments = [...filters.segments, s];

                  setFilters({ ...filters, segments });
                  setPaginationParams({ ...paginationParams, page: 1, segments });
                }}
                onRemove={s => {
                  const segments = filters.segments.filter(s2 => s.key !== s2.key);

                  setFilters({ ...filters, segments });
                  setPaginationParams({ ...paginationParams, page: 1, segments });
                }}
                onClearAll={() => {
                  setFilters({ ...filters, segments: [] });
                  setPaginationParams({ ...paginationParams, page: 1, segments: [] });
                }}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Levels" labelClassName="text-white-i">
              <BubbleTypeahead
                block
                variant="titanium"
                placeholder="Filter levels shown..."
                menuClassName="max-h-80 overflow-y-auto"
                labelExtractor={opt => opt.name}
                value={filters.levels}
                options={levelOpts}
                onAdd={l => {
                  const levels = [...filters.levels, l];

                  setFilters({ ...filters, levels });
                  setPaginationParams({ ...paginationParams, page: 1, levels });
                }}
                onRemove={l => {
                  const levels = filters.levels.filter(l2 => l.key !== l2.key);

                  setFilters({ ...filters, levels });
                  setPaginationParams({ ...paginationParams, page: 1, levels });
                }}
                onClearAll={() => {
                  setFilters({ ...filters, levels: [] });
                  setPaginationParams({ ...paginationParams, page: 1, levels: [] });
                }}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Attributes" labelClassName="text-white-i">
              <BubbleTypeahead
                block
                variant="titanium"
                placeholder="Filter attributes shown..."
                menuClassName="max-h-80 overflow-y-auto"
                labelExtractor={opt => opt.name}
                value={filters.attributes}
                options={attributeOpts}
                onAdd={a => {
                  const attributes = [...filters.attributes, a];

                  setFilters({ ...filters, attributes });
                  setPaginationParams({ ...paginationParams, page: 1, attributes });
                }}
                onRemove={a => {
                  const attributes = filters.attributes.filter(a2 => a.key !== a2.key);

                  setFilters({ ...filters, attributes });
                  setPaginationParams({ ...paginationParams, page: 1, attributes });
                }}
                onClearAll={() => {
                  setFilters({ ...filters, attributes: [] });
                  setPaginationParams({ ...paginationParams, page: 1, attributes: [] });
                }}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Options" labelClassName="text-white-i">
              <div className="grid gap-1">
                <BubbleCheckbox
                  variant="titanium"
                  className="text-white"
                  checked={paginationParams.archived}
                  onValue={(checked: boolean) => {
                    setPaginationParams({ ...paginationParams, page: 1, archived: checked });
                  }}
                  label="View Archived"
                />
              </div>
            </BubbleFormGroup>

            <BubbleFormGroup label="Fields" labelClassName="text-white-i">
              <div className="grid grid-cols-1 gap-1">
                {toggleableTableFields.map(tf => (
                  <BubbleCheckbox
                    key={tf.key}
                    variant="titanium"
                    className="text-white"
                    checked={visibleFields[tf.key]}
                    onValue={chk => setVisibleFields({ ...visibleFields, [tf.key]: chk })}
                    label={tf.label || camelToSentenceCase(tf.key)}
                  />
                ))}
              </div>
            </BubbleFormGroup>
          </div>
        </BubbleAccordion>
      </>
    );
  }

  return (
    <Libraries
      user={user}
      active="libraries"
      property={property}
      loading={loading}
      contentRef={contentRef}
      headerFn={headerFn}
      bodyFn={bodyFn}
      outerFn={outerFn}
      sideFn={sideFn}
      {...routeProps}
    />
  );
}
